// @flow
import React from 'react';
import injectSheet from 'react-jss';
import _ from 'shared/lib/gettext';
import Button from '~/app/components/Button';
import { S, TONES, DARK } from '~/app/ui/tokens';
import io from 'socket.io-client';
import { getAppURL } from 'shared/lib/clientSettings';
import { WEBSOCKET_MSG_ID } from 'shared/constants';

type Props = {
  classes: Object,
  userId: string,
  deployMode: 'ALL' | 'SELECTED',
  selectedVersionIds?: Array<string>,
  cancelBtnEnabled: Boolean,
  version: string,
  onSuccessDeploy: () => void,
  onCancel: () => void,
  onClickDeploy: (string, string, ?Array<string>) => Promise<any>
}

class DeployMode extends React.Component {
  props: Props = this.props;

  // Opens ready: the server says over the socket if a deployment is already running (and refuses a second one,
  // or a version that exists, so nothing here needs to assume one is running).
  state = {
    error: '',
    // The connection to the server, when it isn't fine (shown as a note; deploying still goes through).
    connection: '',
    isDeploying: false,
    // The deployment running was started from this popup (not someone else's, or a schedule).
    startedHere: false,
    progress: 0,
    progressMsg: ''
  };

  static defaultProps = {
    selectedVersionIds: []
  };

  componentDidMount() {
    // Socket used for Redeploy All/Deploy Selected
    this.socket = io(getAppURL(), {
      reconnectionAttempts: 10
    });
    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_STATE, ({ deploying, progress }) => {
      if (this.state.startedHere) return;
      this.setState({ isDeploying: Boolean(deploying), progress: progress || 0 });
    });
    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, this.handleProgress);
    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_ERROR, this.handleError);
    this.socket.on('connect', () => this.setState({ connection: '' }));
    this.socket.on('disconnect', () => this.setState({ connection: `${_('RECONNECTING')}` }));
    // socket.io 4: reconnection events come from the manager, not the socket.
    this.socket.io.on('reconnect_failed', () => this.setState({ connection: `${_('SERVER_FAILURE')}` }));
  }

  componentWillUnmount() {
    this.socket.close();
  }

  handleError = (err: string) => {
    console.log(err);

    const errorMessage = err instanceof Error ? err.message : err;

    // Someone else's deployment failing isn't this popup's error: it's just no longer running.
    this.setState(({ startedHere }) => ({
      error: startedHere ? errorMessage : '',
      progress: 0,
      progressMsg: '',
      isDeploying: false,
      startedHere: false
    }));
  }

  handleClickDeploy = () => {
    const {
      version, deployMode, onClickDeploy, selectedVersionIds
    } = this.props;
    const { isDeploying } = this.state;

    if (!isDeploying && version) {
      this.setState({
        isDeploying: true,
        startedHere: true,
        error: '',
        progress: 0,
        progressMsg: ''
      }, () => {
        const errorHandler = (e) => {
          console.log(`Error during deployment: ${e.stack}`);

          this.setState({
            error: e?.message?.replace('GraphQL error: ', '') || 'Unknown error.',
            progress: 0,
            progressMsg: '',
            isDeploying: false,
            startedHere: false
          });
        };

        if (deployMode === 'ALL') {
          onClickDeploy(version, deployMode)
            .catch(errorHandler);
        } else {
          onClickDeploy(version, deployMode, selectedVersionIds)
            .catch(errorHandler);
        }
      });
    }
  }

  handleProgress = ({ progress, msg }) => {
    const { onSuccessDeploy } = this.props;

    console.log({ progress, msg });
    if (progress >= 1) {
      // The one started here is done: close with success. Someone else's: this popup can deploy now.
      if (this.state.startedHere) {
        this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS);
        this.socket.off(WEBSOCKET_MSG_ID.DEPLOY_ERROR);
        onSuccessDeploy();
      } else {
        this.setState({ isDeploying: false, progress: 0, progressMsg: '' });
      }
    } else {
      this.setState({
        isDeploying: true,
        progress,
        progressMsg: msg
      });
    }
  }

  render() {
    const {
      classes: c, deployMode, cancelBtnEnabled, version, onCancel
    } = this.props;
    const {
      error, connection, isDeploying, startedHere, progress, progressMsg
    } = this.state;
    const warning = error && (
      <div className={c.warningMsg} role="alert">
        <i className="material-icons" aria-hidden>error_outline</i>
        {error}
      </div>
    );
    const label = deployMode === 'ALL' ? 'Redeploy the whole site' : 'Redeploy now';
    const pct = Math.round((progress || 0) * 100);
    const otherRunning = isDeploying && !startedHere;

    return (
      <div className={c.root}>
        <div className={c.versionRow}>
          <span className={c.muted}>Version</span>
          {version
            ? <span className={c.version} data-testid="deploy-mode-version">v{version}</span>
            : (
              <span className={c.waiting} role="status" data-testid="deploy-mode-version-wait">
                <span className={c.spinner} aria-hidden />
                Getting the next version number…
              </span>
            )}
        </div>
        {otherRunning && (
          <div className={c.note} role="status" data-testid="deploy-mode-other">
            Another deployment is running. You can deploy when it finishes.
          </div>
        )}
        {connection && <div className={c.note} role="status">{connection}</div>}
        {warning}
        {isDeploying &&
          <div className={c.progressContainer}>
            <div className={c.bar}><div className={c.barFill} style={{ width: `${Math.max(2, pct)}%` }} /></div>
            <div className={c.progressMsg}>{`${pct}%`}{progressMsg ? ` · ${progressMsg}` : ''}</div>
          </div>
        }
        <div className={c.actions}>
          {cancelBtnEnabled && (
            // Closing is always fine, except while the deployment started here is starting up.
            <Button secondary onClick={onCancel} disabled={startedHere && isDeploying}>
              {_('CANCEL')}
            </Button>
          )}
          {(deployMode === 'ALL' || deployMode === 'REDEPLOY_SELECTED') && (
            <Button
              primary={deployMode !== 'ALL'}
              color={deployMode === 'ALL' ? S.red : undefined}
              onClick={this.handleClickDeploy}
              disabled={isDeploying || !version}
              data-testid="deploy-mode-go"
            >
              {startedHere && isDeploying ? `${_('DEPLOYING')}…` : label}
            </Button>
          )}
        </div>
      </div>
    );
  }
}

const styles = {
  root: { fontFamily: S.ui, color: S.fg },
  versionRow: { display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 12px' },
  muted: { fontSize: 13, color: S.muted },
  version: { fontFamily: S.mono, fontSize: 18, fontWeight: 500 },
  progressContainer: { margin: '12px 0' },
  bar: { height: 6, borderRadius: 3, background: S.chromeHover, overflow: 'hidden' },
  barFill: { height: '100%', background: S.amber, transition: 'width 400ms ease' },
  progressMsg: { marginTop: 6, fontSize: 12, color: S.muted },
  note: { margin: '0 0 10px', fontSize: 13, color: S.muted },
  '@keyframes modeSpin': { to: { transform: 'rotate(360deg)' } },
  waiting: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: S.muted },
  spinner: {
    width: 11,
    height: 11,
    flexShrink: 0,
    borderRadius: '50%',
    border: '2px solid currentColor',
    borderRightColor: 'transparent',
    animation: '$modeSpin 800ms linear infinite'
  },
  warningMsg: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 6,
    background: 'rgba(200, 55, 55, 0.08)',
    color: TONES.red.fg,
    fontSize: 13,
    '& .material-icons': { fontSize: 18 },
    [DARK]: { background: TONES.red.bg }
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 18
  }
};

export default injectSheet(styles)(DeployMode);
